import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Contact, getObjectId, Id } from '@hero/schema';
import { ModalForm } from '../../../app/Modal';
import { ContactForm } from '../Form/ContactForm';
import { ClientLinkingBanner } from '../../Client/LinkingBanner';
import { useContactCreateAdapter } from '@hero/web-hooks';

export interface ContactEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after close animation finishes (for deferred unmount). */
  onExited?: () => void;
  /** Callback when contact is saved/created */
  onContactSaved?: (contact: Contact) => void;
  /** When set, load and edit this contact; when undefined, create mode. */
  contactId?: Id;
  /** For create mode: the customer id to associate the new contact with. */
  customerId?: Id;
  allowOutsideClickClose?: boolean;
}

export function ContactEditModal({
  isOpen,
  onClose,
  onExited,
  onContactSaved,
  contactId,
  customerId,
  allowOutsideClickClose = true,
}: ContactEditModalProps) {
  const { t } = useTranslation(['m-contact', 'c-main']);

  const { contactQuery, createContact, updateContact, formHook, reset, onSaveClick } =
    useContactCreateAdapter({
      onContactSaved,
      contactId,
      customerId,
    });

  // When opening from a Client workspace (`customerId` set, no
  // `contactId` ⇒ create mode), surface the target client at the top
  // of the modal so the user knows which client this contact will be
  // linked to. The shared `<ClientLinkingBanner>` resolves the client
  // by id internally, so we only forward the id we already have.
  const showLinkedClient = contactId == null && customerId != null;

  useEffect(() => {
    if (contactId == null && customerId != null) {
      formHook.setValue('customerId', getObjectId(customerId));
    }
  }, [contactId, customerId, formHook]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const isEdit = contactId != null;
  const isLoadingForm = isEdit && contactQuery.isLoading;
  const isLoading = createContact.isPending || updateContact.isPending;
  const isSuccess = createContact.isSuccess || updateContact.isSuccess;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onExited={onExited}
      onSave={onSaveClick}
      title={isEdit ? t('modal.edit_contact_title') : t('modal.create_contact_title')}
      description={
        isEdit ? t('modal.edit_contact_description') : t('modal.create_contact_description')
      }
      isLoading={isLoading}
      isLoadingForm={isLoadingForm}
      isSuccess={isSuccess}
      allowOutsideClickClose={allowOutsideClickClose && !isLoading}
      hideFooter={false}
      size="md"
      saveText={isEdit ? t('c-main:crud_buttons.update') : t('c-main:crud_buttons.create')}
      disableSave={!formHook.isValid}>
      {showLinkedClient ? (
        <ClientLinkingBanner clientId={customerId} className="mb-4" />
      ) : null}
      <ContactForm formHook={formHook} hideSubmitButton={true} />
    </ModalForm>
  );
}
